import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core'
import { MatDialog } from '@angular/material/dialog'
import {
    AddToWishlistWishlistsGQL,
    AddWishToWishlistCommand,
    AddWishToWishlistGQL,
    RemoveWishFromWishlistCommand,
    RemoveWishFromWishlistGQL, TravelDatesInput,
    Wishlist
} from '../../../../../graphql'
import { dialogWidth } from '../../../../constant'
import { Amplitude } from '../../../../injectable'
import { TravelDates } from '../../../../module/shared-member/organism/listing/shared/travel-dates'
import { generateId, UnsubscribeOnDestroy } from '../../../../utility'
import { CreateWishlistComponent } from '../create-wishlist/create-wishlist'
import { MatSnackBar } from '@angular/material/snack-bar'
import { format } from 'date-fns'

@Component({
    selector: 'app-add-to-wishlist',
    styleUrls: ['add-to-wishlist.scss'],
    templateUrl: 'add-to-wishlist.html'
})
export class AddToWishlistComponent extends UnsubscribeOnDestroy implements OnChanges {
    @Input() listingId: string
    @Input() travelDates: TravelDates
    @Input() useTextButton: boolean

    wishlists: Array<Wishlist>

    @Output() updated = new EventEmitter<WishlistUpdate>()

    constructor(private _amplitude: Amplitude,
                private _dialog: MatDialog,
                private _wishlistsGQL: AddToWishlistWishlistsGQL,
                private _addToWishlistGQL: AddWishToWishlistGQL,
                private _removeFromWishlistGQL: RemoveWishFromWishlistGQL,
                private _snackBar: MatSnackBar) {
        super()
        this.useTextButton = !!this.useTextButton 
        this.refresh()
    }
               
    ngOnChanges(changes: SimpleChanges): void {
        if (changes?.listingId) {
            this.listingId = changes.listingId.currentValue
            this.refresh()
        }
    }

    getTravelDateAsStrings() {
        let checkin = ''
        let checkout = ''

        if (this.travelDates?.checkin && this.travelDates?.checkout) {
            checkin = format(this.travelDates.checkin, 'yyyy-MM-dd')
            checkout = format(this.travelDates.checkout, 'yyyy-MM-dd')
        }

        return {
            checkin,
            checkout
        }
    }

    containsListing(wishlist: Wishlist) {
        const { checkin, checkout } = this.getTravelDateAsStrings()

        return wishlist.wishes.some(wish => {
            const wishCheckin = wish?.travelDates?.checkin || ''
            const wishCheckout = wish?.travelDates?.checkout || ''
            return (wish.searchListing.listingId + wishCheckin + wishCheckout) === (this.listingId + checkin + checkout)
        })
    }

    addToWishlist($event: Event, wishlistId: string) {
        $event.stopPropagation()

        const { checkin, checkout } = this.getTravelDateAsStrings()

        const command: AddWishToWishlistCommand = {
            wishlistId: wishlistId,
            wishId: generateId('wish'),
            listingId: this.listingId,
            notes: null,
            ...((checkin && checkout) && {travelDates: {
                checkin: checkin,
                checkout: checkout
            }})
        }
        this._addToWishlistGQL.mutate({input: command}).subscribe(() => {
            this._amplitude.logEvent('Listing Added To Wishlist', {
                wishlistId: wishlistId,
                wishId: command.wishId,
                listingId: this.listingId,
                hasTravelDates: !!this.travelDates
            })

            this.updated.next({
                wishlistId: wishlistId,
                added: true,
            })
            this.refresh()

            this._snackBar.open('Listing added to proposal')
        })
    }

    removeFromWishlist($event: Event, wishlist: Wishlist) {
        $event.stopPropagation();

        const { checkin, checkout } = this.getTravelDateAsStrings()

        const wishId = wishlist.wishes.find(wish => {
            const wishCheckin = wish?.travelDates?.checkin || ''
            const wishCheckout = wish?.travelDates?.checkout || ''

            return (wish.searchListing.listingId + wishCheckin + wishCheckout) === (this.listingId + checkin + checkout)
        })?.wishId

        if (!wishId) {
            return
        }

        const command: RemoveWishFromWishlistCommand = {
            wishlistId: wishlist.wishlistId,
            wishId: wishId,
        }
        this._removeFromWishlistGQL.mutate({input: command}).subscribe(() => {
            this._amplitude.logEvent('Listing Removed From Wishlist', {
                wishlistId: wishlist.wishlistId,
                wishId: wishId,
                listingId: this.listingId
            })

            this.updated.next({
                wishlistId: wishlist.wishlistId,
                added: false,
            })
            this.refresh()

            this._snackBar.open('Listing removed from proposal')
        })
    }

    openCreateShortlistDialog($event: Event): void {
        const dialogRef = this._dialog.open(CreateWishlistComponent, {
            width: dialogWidth.xs
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.addToWishlist($event, result)
            }
        });

        $event.stopPropagation();
    }

    refresh() {
        this._subscriptions.push(this._wishlistsGQL.fetch({}, {fetchPolicy: 'no-cache'})
            .subscribe(result => {
                if (!result.loading) {
                    if (result.errors?.length) {
                        throw new Error(`Failed to fetch wishlists for listing: ${result.errors[0].message}`)
                    }
                    this.wishlists = result.data.wishlists as Array<Wishlist>
                    this.wishlists.sort((a, b) => a.name.localeCompare(b.name))
                }
            }))
    }
}

export interface WishlistUpdate {
    wishlistId: string
    added: boolean,
}
